/**
 * Shuttle admin API client. All calls go through `apiFetch` so the bearer
 * JWT is attached automatically; shuttle-svc enforces role=admin.
 *
 * @module api/shuttle
 */
import { apiFetch } from "../auth.ts";
import type {
	DeviceInfo,
	DeviceProfileInfo,
	MapLayer,
	PickupPoint,
	POI,
	QuotaSnapshot,
	RouteWaypoint,
	ScheduleEntry,
	ScheduleOverride,
	ShuttleRoute,
	ShuttleVehicle,
	Tenant,
	TenantApp,
	TenantAppCreated,
	UnregisteredDevice,
	VehicleKey,
	VehicleKeyCreated,
} from "../types/shuttle.ts";

/** Base URL for admin endpoints scoped to a tenant slug. */
function adminBase(slug: string): string {
	return `/shuttle/v1/admin/tenants/${encodeURIComponent(slug)}`;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
	if (!res.ok) {
		const body = await res.text();
		throw new Error(`${res.status} ${res.statusText}: ${body}`);
	}
	if (res.status === 204) return undefined as T;
	return (await res.json()) as T;
}

// ─── Tenants ────────────────────────────────────────────────────────────────
// Note: tenant CRUD is not exposed by shuttle-svc admin API yet — only the
// `/quotas` endpoint is per-tenant. The dashboard hard-codes the available
// tenant list for now (see panels/shuttle/index.ts).

export async function getQuotas(slug: string): Promise<QuotaSnapshot> {
	return jsonOrThrow<QuotaSnapshot>(
		await apiFetch(`${adminBase(slug)}/quotas`),
	);
}

// ─── Pickup points ──────────────────────────────────────────────────────────

export async function listPickupPoints(slug: string): Promise<PickupPoint[]> {
	return jsonOrThrow<PickupPoint[]>(
		await apiFetch(`${adminBase(slug)}/pickup-points`),
	);
}

export async function createPickupPoint(
	slug: string,
	body: {
		name: string;
		description?: string;
		lat: number;
		lng: number;
		metadata?: Record<string, unknown> | null;
	},
): Promise<PickupPoint> {
	return jsonOrThrow<PickupPoint>(
		await apiFetch(`${adminBase(slug)}/pickup-points`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

export async function updatePickupPoint(
	slug: string,
	id: string,
	patch: Partial<
		Pick<
			PickupPoint,
			| "name"
			| "description"
			| "lat"
			| "lng"
			| "is_active"
			| "is_enabled"
			| "detection_radius_m"
			| "detection_frames"
			| "metadata"
		>
	>,
): Promise<PickupPoint> {
	return jsonOrThrow<PickupPoint>(
		await apiFetch(`${adminBase(slug)}/pickup-points/${id}`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(patch),
		}),
	);
}

export async function deletePickupPoint(
	slug: string,
	id: string,
): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/pickup-points/${id}`, {
			method: "DELETE",
		}),
	);
}

// ─── Schedules ──────────────────────────────────────────────────────────────

export interface ScheduleListResponse {
	entries: ScheduleEntry[];
}

export async function listSchedule(
	slug: string,
	pickupID: string,
): Promise<ScheduleEntry[]> {
	const r = await jsonOrThrow<ScheduleListResponse>(
		await apiFetch(`${adminBase(slug)}/pickup-points/${pickupID}/schedule`),
	);
	return r.entries ?? [];
}

export async function replaceSchedule(
	slug: string,
	pickupID: string,
	entries: ScheduleEntry[],
): Promise<ScheduleEntry[]> {
	const r = await jsonOrThrow<ScheduleListResponse>(
		await apiFetch(`${adminBase(slug)}/pickup-points/${pickupID}/schedule`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ entries }),
		}),
	);
	return r.entries ?? [];
}

export async function createOverride(
	slug: string,
	pickupID: string,
	override: ScheduleOverride,
): Promise<ScheduleOverride> {
	return jsonOrThrow<ScheduleOverride>(
		await apiFetch(
			`${adminBase(slug)}/pickup-points/${pickupID}/schedule/overrides`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(override),
			},
		),
	);
}

export async function deleteOverride(
	slug: string,
	pickupID: string,
	overrideID: string,
): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(
			`${adminBase(slug)}/pickup-points/${pickupID}/schedule/overrides/${overrideID}`,
			{
				method: "DELETE",
			},
		),
	);
}

// ─── Routes ─────────────────────────────────────────────────────────────────

export async function listRoutes(slug: string): Promise<ShuttleRoute[]> {
	return jsonOrThrow<ShuttleRoute[]>(
		await apiFetch(`${adminBase(slug)}/routes`),
	);
}

/** List routes via the public (unauthenticated) tenant endpoint — safe to call
 *  from non-admin contexts such as the simulator panel. */
export async function listRoutesPublic(slug: string): Promise<ShuttleRoute[]> {
	return jsonOrThrow<ShuttleRoute[]>(
		await fetch(`/shuttle/v1/tenants/${encodeURIComponent(slug)}/routes`),
	);
}

export async function createRoute(
	slug: string,
	body: {
		name: string;
		color?: string;
		polyline: { lat: number; lng: number }[];
		stop_ids?: string[];
		waypoints?: RouteWaypoint[];
		metadata?: Record<string, unknown> | null;
	},
): Promise<ShuttleRoute> {
	return jsonOrThrow<ShuttleRoute>(
		await apiFetch(`${adminBase(slug)}/routes`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

export async function updateRoute(
	slug: string,
	id: string,
	body: {
		name?: string;
		color?: string;
		polyline?: { lat: number; lng: number }[];
		waypoints?: RouteWaypoint[];
		is_enabled?: boolean;
		metadata?: Record<string, unknown> | null;
	},
): Promise<ShuttleRoute> {
	return jsonOrThrow<ShuttleRoute>(
		await apiFetch(`${adminBase(slug)}/routes/${id}`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

/**
 * Real-time OSRM preview for the route editor. Sends an ordered list of
 * coordinates (stops + transit) and returns the road-snapped polyline so the
 * editor can show what the shuttle will actually drive while the user is
 * still placing waypoints.
 */
export async function previewRoute(
	slug: string,
	waypoints: { lat: number; lng: number }[],
	opts?: { loop?: boolean; bidirectional?: boolean },
): Promise<{
	polyline: { lat: number; lng: number }[];
	reverse_polyline?: { lat: number; lng: number }[];
	source: "osrm" | "stored";
	distance_m?: number;
	duration_sec?: number;
}> {
	return jsonOrThrow(
		await apiFetch(`${adminBase(slug)}/routes/preview`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				waypoints,
				loop: opts?.loop ?? false,
				bidirectional: opts?.bidirectional ?? false,
			}),
		}),
	);
}

export async function getRouteGeometry(
	slug: string,
	id: string,
): Promise<{
	polyline: { lat: number; lng: number }[];
	reverse_polyline?: { lat: number; lng: number }[];
	source: "osrm" | "stored";
	bidirectional?: boolean;
}> {
	return jsonOrThrow(
		await apiFetch(`${adminBase(slug)}/routes/${id}/geometry`),
	);
}

/** Public (no-auth) equivalent of getRouteGeometry. */
export async function getRouteGeometryPublic(
	slug: string,
	id: string,
): Promise<{
	polyline: { lat: number; lng: number }[];
	reverse_polyline?: { lat: number; lng: number }[];
	source: "osrm" | "stored";
	bidirectional?: boolean;
}> {
	return jsonOrThrow(
		await fetch(
			`/shuttle/v1/tenants/${encodeURIComponent(slug)}/routes/${encodeURIComponent(id)}/geometry`,
		),
	);
}

export async function deleteRoute(slug: string, id: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/routes/${id}`, {
			method: "DELETE",
		}),
	);
}

// ─── Layers ─────────────────────────────────────────────────────────────────

export async function listLayers(slug: string): Promise<MapLayer[]> {
	return jsonOrThrow<MapLayer[]>(await apiFetch(`${adminBase(slug)}/layers`));
}

export async function createLayer(
	slug: string,
	body: {
		name: string;
		kind: string;
		style?: Record<string, unknown> | null;
		payload: Record<string, unknown> | unknown[];
		z_index?: number;
	},
): Promise<MapLayer> {
	return jsonOrThrow<MapLayer>(
		await apiFetch(`${adminBase(slug)}/layers`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

export async function deleteLayer(slug: string, id: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/layers/${id}`, {
			method: "DELETE",
		}),
	);
}

// ─── Integration keys (apps) ────────────────────────────────────────────────

export async function listApps(slug: string): Promise<TenantApp[]> {
	return jsonOrThrow<TenantApp[]>(await apiFetch(`${adminBase(slug)}/apps`));
}

export async function createApp(
	slug: string,
	body: {
		app_identifier: string;
		label?: string;
		env?: "live" | "test";
	},
): Promise<TenantAppCreated> {
	return jsonOrThrow<TenantAppCreated>(
		await apiFetch(`${adminBase(slug)}/apps`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

export async function revokeApp(slug: string, id: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/apps/${id}`, {
			method: "DELETE",
		}),
	);
}

export async function fetchAppKey(
	slug: string,
	id: string,
	password: string,
): Promise<{ key_secret: string }> {
	return jsonOrThrow<{ key_secret: string }>(
		await apiFetch(`${adminBase(slug)}/apps/${id}/fetch-key`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ password }),
		}),
	);
}

export async function rerollAppKey(
	slug: string,
	id: string,
	password: string,
	integrationKey: string,
): Promise<{ key_secret: string }> {
	return jsonOrThrow<{ key_secret: string }>(
		await apiFetch(`${adminBase(slug)}/apps/${id}/reroll-key`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ password, integration_key: integrationKey }),
		}),
	);
}

export async function deleteAppSecure(
	slug: string,
	id: string,
	password: string,
	integrationKey: string,
): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/apps/${id}/delete`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ password, integration_key: integrationKey }),
		}),
	);
}

// ─── Tenant routing settings ────────────────────────────────────────────────

export async function getTenantSettings(slug: string): Promise<Tenant> {
	return jsonOrThrow<Tenant>(await apiFetch(`${adminBase(slug)}/settings`));
}

export interface RoutingSettingsPatch {
	google_maps_api_key?: string; // "" = clear key (use global)
	walk_routing_provider?: string; // "google" | "osrm" | "off" | ""
	drive_routing_provider?: string; // "google" | "osrm" | ""
	map_tile_provider?: string; // "google" | "osm" | ""
	google_mode_enabled?: boolean; // app-only passthrough flag (mobile reads it)
	shift_start?: string; // "HH:MM" — operator shift start in tenant timezone
	shift_duration_hours?: number; // shift window length in hours
	timezone?: string; // IANA timezone string e.g. "Africa/Cairo"
}

export async function updateRoutingSettings(
	slug: string,
	patch: RoutingSettingsPatch,
): Promise<Tenant> {
	return jsonOrThrow<Tenant>(
		await apiFetch(`${adminBase(slug)}/settings`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(patch),
		}),
	);
}

// ─── Cache ──────────────────────────────────────────────────────────────────

export async function purgeCache(slug: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/cache/purge`, {
			method: "POST",
		}),
	);
}

// ─── Devices (physical VTU units) ──────────────────────────────────────────

export async function listDevices(slug: string): Promise<DeviceInfo[]> {
	return jsonOrThrow<DeviceInfo[]>(
		await apiFetch(`${adminBase(slug)}/devices`),
	);
}

/**
 * Devices turned away at the gateway because nothing has registered them.
 *
 * This is how an installer finds the IMEI of a device that is not reporting:
 * it is refused before it sends anything, and the number is on a label inside
 * the vehicle.
 */
export async function listUnregisteredDevices(
	slug: string,
): Promise<UnregisteredDevice[]> {
	return jsonOrThrow<UnregisteredDevice[]>(
		await apiFetch(`${adminBase(slug)}/devices/unregistered`),
	);
}

/**
 * Stores a custom device type from a profile document. The backend validates it
 * with the same parser the shipped profiles go through, so one it accepts is one
 * that can be promoted to a built-in unchanged.
 */
export async function createDeviceProfile(
	slug: string,
	document: string,
): Promise<DeviceProfileInfo> {
	const res = await apiFetch(`${adminBase(slug)}/device-profiles`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: document,
	});
	if (!res.ok) throw new Error(await res.text());
	return (await res.json()) as DeviceProfileInfo;
}

/**
 * The domain destinations a signal may name. Sourced from the backend so the
 * profile builder offers exactly what this build accepts.
 */
export async function listProfileTargets(slug: string): Promise<string[]> {
	return jsonOrThrow<string[]>(
		await apiFetch(`${adminBase(slug)}/device-profiles/targets`),
	);
}

export async function deleteDeviceProfile(
	slug: string,
	key: string,
): Promise<void> {
	const res = await apiFetch(`${adminBase(slug)}/device-profiles/${key}`, {
		method: "DELETE",
	});
	if (!res.ok) throw new Error(await res.text());
}

/**
 * Downloads a device type as the file that makes it a built-in. The key is
 * already protocol/model, which is exactly the path the endpoint expects.
 */
export async function exportDeviceProfile(
	slug: string,
	key: string,
): Promise<{ filename: string; body: string }> {
	const res = await apiFetch(
		`${adminBase(slug)}/device-profiles/${key}/export`,
	);
	if (!res.ok) throw new Error(await res.text());
	const model = key.split("/").pop() ?? key;
	return { filename: `${model}.json`, body: await res.text() };
}

/**
 * Device models this deployment can decode. Sourced from the running backend
 * rather than a constant here: which models it knows is a property of the build
 * being talked to.
 */
export async function listDeviceProfiles(
	slug: string,
): Promise<DeviceProfileInfo[]> {
	return jsonOrThrow<DeviceProfileInfo[]>(
		await apiFetch(`${adminBase(slug)}/device-profiles`),
	);
}

/**
 * profileKey is required. An IO element id means different things on different
 * models, so a device registered under the wrong one reports plausible but
 * wrong numbers rather than failing — which is why there is no default.
 */
export async function registerDevice(
	slug: string,
	imei: string,
	profileKey: string,
	phoneNumber?: string,
	isSimulated = false,
): Promise<void> {
	const res = await apiFetch(`${adminBase(slug)}/devices`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			imei,
			profile_key: profileKey,
			phone_number: phoneNumber,
			is_simulated: isSimulated,
		}),
	});
	if (!res.ok) throw new Error(await res.text());
}

/** An empty profileKey leaves the device's model unchanged. */
export async function updateDevice(
	slug: string,
	imei: string,
	phoneNumber: string,
	profileKey = "",
): Promise<void> {
	const res = await apiFetch(
		`${adminBase(slug)}/devices/${encodeURIComponent(imei)}`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				phone_number: phoneNumber,
				profile_key: profileKey,
			}),
		},
	);
	if (!res.ok) throw new Error(await res.text());
}

export async function deregisterDevice(
	slug: string,
	imei: string,
): Promise<void> {
	const res = await apiFetch(
		`${adminBase(slug)}/devices/${encodeURIComponent(imei)}`,
		{
			method: "DELETE",
		},
	);
	if (!res.ok) throw new Error(await res.text());
}

// ─── Vehicles (live carts) ──────────────────────────────────────────────────

export async function listVehicles(slug: string): Promise<ShuttleVehicle[]> {
	return jsonOrThrow<ShuttleVehicle[]>(
		await apiFetch(`${adminBase(slug)}/vehicles`),
	);
}

export async function createVehicle(
	slug: string,
	body: {
		label: string;
		imei?: string | null;
		license_plate?: string | null;
		route_id?: string | null;
		has_telemetry_device?: boolean;
		metadata?: Record<string, unknown> | null;
	},
): Promise<ShuttleVehicle> {
	return jsonOrThrow<ShuttleVehicle>(
		await apiFetch(`${adminBase(slug)}/vehicles`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

export async function updateVehicle(
	slug: string,
	id: string,
	patch: {
		label?: string;
		imei?: string | null;
		clear_imei?: boolean;
		license_plate?: string | null;
		clear_license_plate?: boolean;
		route_id?: string | null;
		clear_route?: boolean;
		is_active?: boolean;
		has_telemetry_device?: boolean;
		metadata?: Record<string, unknown> | null;
	},
): Promise<ShuttleVehicle> {
	return jsonOrThrow<ShuttleVehicle>(
		await apiFetch(`${adminBase(slug)}/vehicles/${id}`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(patch),
		}),
	);
}

export async function deleteVehicle(slug: string, id: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/vehicles/${id}`, { method: "DELETE" }),
	);
}

export async function listVehicleKeys(
	slug: string,
	vehicleID: string,
): Promise<VehicleKey[]> {
	return jsonOrThrow<VehicleKey[]>(
		await apiFetch(`${adminBase(slug)}/vehicles/${vehicleID}/keys`),
	);
}

export async function mintVehicleKey(
	slug: string,
	vehicleID: string,
	label?: string,
): Promise<VehicleKeyCreated> {
	return jsonOrThrow<VehicleKeyCreated>(
		await apiFetch(`${adminBase(slug)}/vehicles/${vehicleID}/keys`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ label: label ?? "" }),
		}),
	);
}

export async function revokeVehicleKey(
	slug: string,
	vehicleID: string,
	keyID: string,
): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/vehicles/${vehicleID}/keys/${keyID}`, {
			method: "DELETE",
		}),
	);
}

// ─── Operator: driver assignment ────────────────────────────────────────

/** Driver picker option, sourced from auth-svc /admin/users?role=driver. */
export interface DriverOption {
	id: string;
	username?: string | null;
	email?: string | null;
	driver_status?: string | null;
}

/** Lists drivers via auth-svc admin endpoint. Admin or operator JWT required. */
export async function listDrivers(): Promise<DriverOption[]> {
	const res = await apiFetch(`/auth/admin/users?role=driver&limit=500`);
	if (!res.ok) {
		const body = await res.text();
		throw new Error(`${res.status} ${res.statusText}: ${body}`);
	}
	const data = (await res.json()) as { users?: DriverOption[] };
	return data.users ?? [];
}

export async function assignDriver(
	vehicleID: string,
	driverUserID: string,
): Promise<ShuttleVehicle> {
	return jsonOrThrow<ShuttleVehicle>(
		await apiFetch(`/shuttle/v1/operator/vehicles/${vehicleID}/driver`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ driver_user_id: driverUserID }),
		}),
	);
}

export async function unassignDriver(vehicleID: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`/shuttle/v1/operator/vehicles/${vehicleID}/driver`, {
			method: "DELETE",
		}),
	);
}

// ─── POIs ────────────────────────────────────────────────────────────────────

export async function listPOIs(slug: string): Promise<POI[]> {
	return jsonOrThrow<POI[]>(await apiFetch(`${adminBase(slug)}/pois`));
}

export interface POICreateBody {
	name: string;
	description?: string;
	category?: string;
	lat: number;
	lng: number;
	metadata?: Record<string, unknown>;
}

export async function createPOI(
	slug: string,
	body: POICreateBody,
): Promise<POI> {
	return jsonOrThrow<POI>(
		await apiFetch(`${adminBase(slug)}/pois`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),
	);
}

export interface POIPatchBody {
	name?: string;
	description?: string;
	category?: string;
	lat?: number;
	lng?: number;
	is_active?: boolean;
	is_enabled?: boolean;
	metadata?: Record<string, unknown>;
}

export async function updatePOI(
	slug: string,
	id: string,
	patch: POIPatchBody,
): Promise<POI> {
	return jsonOrThrow<POI>(
		await apiFetch(`${adminBase(slug)}/pois/${encodeURIComponent(id)}`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(patch),
		}),
	);
}

export async function deletePOI(slug: string, id: string): Promise<void> {
	await jsonOrThrow<void>(
		await apiFetch(`${adminBase(slug)}/pois/${encodeURIComponent(id)}`, {
			method: "DELETE",
		}),
	);
}
